import React from "react";
import { CV } from "../components/cv";
import MartinLImg from "@/assets/images/martin-l.png";

export default function CVMartinLevin() {
  React.useEffect(() => {
    document.title = "BoldBrave CV - Martin Levin";
  }, []);

  return (
    <CV
      name="Martin Levin"
      photo={MartinLImg}
      title="Senior Program & Project Manager · Agile Product Owner"
      subtitle="20+ years in IT, 14+ in leadership roles. Business-critical programs in banking and retail/e-commerce across multi-team, vendor-heavy environments."
      quote={{
        text: "Martin drives initiatives in a professional and reliable way towards clear goals and business value. A true team player with strong communication skills.",
        author: "Business Stakeholder",
        authorTitle: "Client testimonial",
      }}
      summary="Senior IT consultant with 20+ years of experience, including 14+ years in leadership roles as Program Manager, Project Manager and Agile Product Owner. Proven track record of leading complex, business-critical initiatives in banking and retail/e-commerce, delivering results in multi-team, vendor-heavy and fast-changing environments. Recognised for a pragmatic and results-oriented leadership style, with a strong ability to align stakeholders, navigate uncertainty and ensure steady progress towards business goals. Known for combining strategic thinking with hands-on execution, consistently driving clarity, momentum and business value."
      skills={{
        "Program & Project Management": [
          "Agile",
          "SAFe",
          "Hybrid delivery",
          "Governance & Change Control Board",
          "Risk & deviation management",
          "Progress reporting",
        ],
        "Product Ownership": [
          "Backlog management",
          "Prioritisation",
          "System ownership",
          "Budgeting & financial follow-up",
        ],
        Leadership: [
          "Stakeholder & executive communication",
          "Cross-functional leadership",
          "Change management",
          "Agile coaching & SAFe implementation",
          "Digital transformation",
        ],
        "Vendor & Procurement": [
          "Vendor management",
          "RFI / RFP",
          "Vendor selection",
          "Contract finalisation",
        ],
        Domains: [
          "Banking & lending",
          "Payments (PSD2, P27)",
          "Fraud prevention",
          "Core banking transformation",
          "Retail & e-commerce",
        ],
        "Tools & Technologies": [
          "Jira",
          "Jira Align",
          "Confluence",
          "Azure DevOps",
          "MS Project",
          "Miro",
          "Lucidchart",
          "Visio",
          "Teams",
          "ChatGPT",
        ],
        Languages: ["Swedish (native)", "English (fluent)"],
      }}
      experience={[
        {
          role: "Project Manager – UK Loan Product",
          company: "Ikano Bank",
          period: "Sep 2025 – May 2026",
          description:
            "Leading a business-critical transformation program within lending and payments in the UK market.",
          highlights: [
            "Driving end-to-end delivery across multiple teams, suppliers and stakeholders.",
            "Ensuring alignment between business goals, compliance and technical execution.",
            "Establishing structure and governance for predictable delivery and transparency towards senior stakeholders.",
            "Managing dependencies across systems supporting the loan lifecycle, payments and customer journeys.",
            "Proactively identifying risks and driving mitigation actions in a complex environment.",
          ],
        },
        {
          role: "Project Manager – Swedish Loan Product",
          company: "Ikano Bank",
          period: "May 2025 – Sep 2025",
          description:
            "Delivery of a Swedish loan product across several agile release trains and an external supplier.",
          highlights: [
            "Coordinated delivery across four SAFe ARTs and an external supplier.",
            "Managed progress reporting, risk mitigation and deviation handling.",
            "Facilitated Change Control Board governance.",
          ],
        },
        {
          role: "Project Manager – Core Banking Transformation",
          company: "Ikano Bank",
          period: "Sep 2023 – May 2025",
          description:
            "Key contributor to a successful core banking system replacement, delivered on time without major incidents.",
          highlights: [
            "Orchestrated delivery and integrations across four SAFe ARTs and external vendors.",
            "Managed a complex IT landscape with multiple cross-system dependencies.",
            "Acted as main escalation point for teams and suppliers.",
            "Initiated and led a production smoke verification, identifying critical Go-Live issues before launch.",
            "Provided continuous progress reporting and managed deviations.",
            "Impact: secured a stable and successful go-live in a complex, high-risk transformation program.",
          ],
        },
        {
          role: "Agile Product Owner / Agile Coach",
          company: "Tunstall",
          period: "Sep 2022 – May 2023",
          description:
            "Product ownership for Identity & Access Management capabilities and agile coaching across markets.",
          highlights: [
            "Owned and prioritised the backlog for IAM capabilities.",
            "Collaborated with stakeholders across UK, Spain and Sweden.",
            "Coached the organisation in SAFe implementation and DevOps ways of working.",
            "Supported the setup of a new Agile Release Train.",
          ],
        },
        {
          role: "Agile Product Owner – Payments & Fraud Prevention",
          company: "Handelsbanken",
          period: "Dec 2019 – Aug 2022",
          description:
            "Backlog and system ownership for fraud prevention and payment solutions, including P27.",
          highlights: [
            "Owned the backlog for fraud prevention and payment solutions (incl. P27).",
            "System owner for the fraud prevention platform.",
            "Introduced monitoring and controls that reduced fraud exposure for customers.",
            "Managed budgeting and financial follow-up.",
            "Drove alignment across multiple teams and Product Owners.",
          ],
        },
        {
          role: "Project Manager – Fraud Prevention Platform",
          company: "Handelsbanken",
          period: "Oct 2018 – Dec 2019",
          description:
            "Led development of a global fraud prevention solution compliant with PSD2.",
          highlights: [
            "Managed rollout across four markets.",
            "Coordinated development in nine upstream payment systems.",
            "Led a cross-functional team of 14 specialists.",
            "Impact: delivered a business-critical compliance solution across multiple markets.",
          ],
        },
        {
          role: "Project Manager – Infrastructure Sourcing & Deployment",
          company: "Handelsbanken",
          period: "Feb 2018 – Dec 2018",
          description:
            "End-to-end sourcing and deployment of a third-party infrastructure component enabling integration with a new Clearing and Settlement Mechanism.",
          highlights: [
            "Managed the full procurement lifecycle (RFI/RFP), including vendor selection, stakeholder alignment and contract finalisation.",
            "Delivered the initiative from sourcing to production deployment within nine months in a regulated banking environment.",
          ],
        },
        {
          role: "Business Developer / Project Manager",
          company: "IKEA Retail Services",
          period: "Earlier",
          description:
            "Business development and delivery within retail and e-commerce.",
          highlights: [
            "Improved checkout conversion through contextual chat.",
            "Led development of mobile app features (in-store navigation and contextual offers).",
          ],
        },
        {
          role: "Project Manager",
          company: "Yara International",
          period: "Earlier",
          description:
            "Transitioned a project into managed service, including ITIL and SLA/OLA setup.",
        },
        {
          role: "Developer & Technical Lead",
          company: "Husqvarna · IKEA IT · Sony Ericsson · Ericsson",
          period: "Earlier",
          description:
            "Extensive background in mobile development, technical leadership and system development.",
        },
      ]}
      education={[
        {
          degree: "M.Sc. Electrical Engineering",
          institution: "Lund University",
          year: "",
        },
        {
          degree: "B.Sc. Electrical Engineering",
          institution: "Malmö University",
          year: "",
        },
      ]}
      certifications={[
        { name: "SAFe Product Owner / Product Manager" },
        { name: "Certified Scrum Master" },
      ]}
    />
  );
}
